import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

interface Props {
  children: React.ReactNode;
}

export default function PageTransitionWrapper({ children }: Props) {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const [barProgress, setBarProgress] = useState(0);
  const [barVisible, setBarVisible] = useState(false);

  useEffect(() => {
    // Skip on initial mount — only animate on actual navigation
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;

    // Scroll to top immediately on route change
    window.scrollTo({ top: 0, behavior: "instant" });

    // Fire the top progress bar
    setBarVisible(true);
    setBarProgress(0);

    const t1 = setTimeout(() => setBarProgress(55), 20);
    const t2 = setTimeout(() => setBarProgress(85), 160);
    const t3 = setTimeout(() => setBarProgress(100), 300);
    const t4 = setTimeout(() => {
      setBarVisible(false);
    }, 480);
    const t5 = setTimeout(() => setBarProgress(0), 720);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [location.pathname]);

  return (
    <>
      {/* ── Top progress bar ─────────────────────── */}
      <div
        className="fixed top-0 left-0 w-full pointer-events-none"
        style={{ height: "2px", zIndex: 9999 }}
        aria-hidden="true"
      >
        <div
          style={{
            height: "2px",
            width: `${barProgress}%`,
            background: "var(--color-brand)",
            opacity: barVisible ? 1 : 0,
            transition: barVisible
              ? "width 0.28s cubic-bezier(0.22, 1, 0.36, 1)"
              : "opacity 0.22s ease 0.05s",
          }}
        />
      </div>

      {/* ── Page content — no mount animation ─────── */}
      <div>
        {children}
      </div>
    </>
  );
}